import { useState } from 'react'
import type { CSSProperties, JSX } from 'react'

import {
  Button,
  ChevronDownIcon,
  ChevronUpIcon,
  Collapsible,
  Flex,
  Heading,
  Image,
  Link,
  ArrowRightIcon,
  Notice,
  Text,
  useIsDarkMode,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext/hooks'

import type { TourStep as TourStepType } from '../../useOnboardingFlow'

import styles from './TourStep.module.css'

/**
 * @hardcoded S3 base URL and slug-to-image mappings below (`TOUR_IMAGES`). The
 *   mapping is not 1:1 (e.g. `'idea-board'` -> `create.png`). A new tour item from
 *   the API renders with no image.
 * @fix Add `imageUrl` to `TourStepItem` in the GraphQL schema, populated with the
 *   S3 URLs in the backend. Remove `TOUR_IMAGES`, `BASE_URL`, and `buildTourImage`.
 */
const BASE_URL = 'https://buffer-publish.s3.amazonaws.com/images/onboarding'

type TourImageVariant = {
  src: string
  srcSet: Array<{ srcSet: string; media: string; sizes: string }>
}

type TourImage = {
  light: TourImageVariant
  dark: TourImageVariant
}

type TourImageWidths = [number, number, number, number]

function buildTourImage(
  name: string,
  widths: { light: TourImageWidths; dark: TourImageWidths } = {
    light: [720, 1440, 2160, 2880],
    dark: [720, 1440, 2160, 2880],
  },
): TourImage {
  const sources = (
    suffix: string,
    [baseWidth, width2x, width3x, width4x]: TourImageWidths,
  ): TourImageVariant['srcSet'] => [
    {
      srcSet: `${BASE_URL}/illustration-${name}${suffix}.png ${baseWidth}w, ${BASE_URL}/illustration-${name}${suffix}@2x.png ${width2x}w, ${BASE_URL}/illustration-${name}${suffix}@3x.png ${width3x}w, ${BASE_URL}/illustration-${name}${suffix}@4x.png ${width4x}w`,
      media: 'all',
      sizes: '(min-width: 1200px) 50vw, 100vw',
    },
  ]

  return {
    light: {
      src: `${BASE_URL}/illustration-${name}.png`,
      srcSet: sources('', widths.light),
    },
    dark: {
      src: `${BASE_URL}/illustration-${name}-dark.png`,
      srcSet: sources('-dark', widths.dark),
    },
  }
}

type TourSlug =
  | 'idea-board'
  | 'templates'
  | 'compose-and-post'
  | 'reply-to-comments'

type TourStyle = CSSProperties & {
  '--tour-accent-color'?: string
  '--tour-accent-border-color'?: string
}

const TOUR_IMAGES: Record<TourSlug, TourImage> = {
  'idea-board': buildTourImage('create'),
  templates: buildTourImage('templates', {
    light: [725, 1450, 2175, 2900],
    dark: [720, 1440, 2160, 2880],
  }),
  'compose-and-post': buildTourImage('schedule', {
    light: [728, 1456, 2184, 2911],
    dark: [720, 1440, 2160, 2880],
  }),
  'reply-to-comments': buildTourImage('community'),
}

/**
 * @hardcoded Accent and border colours for the four known tour slugs. A new tour
 *   item from the API renders with no accent colour.
 * @fix Add `accentColor` to `TourStepItem` in the GraphQL schema. Remove both
 *   `TOUR_COLORS` and `TOUR_BORDER_COLORS`.
 */
const TOUR_COLORS: Record<TourSlug, string> = {
  'idea-board': 'var(--global-color-blue-a-200)',
  templates: 'var(--global-color-yellow-a-200)',
  'compose-and-post': 'var(--global-color-purple-a-200)',
  'reply-to-comments': 'var(--global-color-orange-a-200)',
}

const TOUR_BORDER_COLORS: Record<TourSlug, string> = {
  'idea-board': 'var(--global-color-blue-600)',
  templates: 'var(--global-color-yellow-600)',
  'compose-and-post': 'var(--global-color-purple-600)',
  'reply-to-comments': 'var(--global-color-orange-600)',
}

function isTourSlug(slug: string): slug is TourSlug {
  return slug in TOUR_IMAGES
}

interface TourStepProps {
  step: TourStepType
  loading?: boolean
  error?: string | null
  onDismissError?: () => void
  onSubmit: (answers: string[]) => void
  flowSlug: string | null
  flowVersion: number | null
}

export function TourStep({
  step,
  loading,
  error,
  onDismissError,
  onSubmit,
  flowSlug,
  flowVersion,
}: TourStepProps): JSX.Element {
  const [openSlug, setOpenSlug] = useState<string>(step.items[0]?.slug ?? '')
  const isDarkMode = useIsDarkMode()
  const organizationId = useOrganizationId()

  const accentColor = isTourSlug(openSlug) ? TOUR_COLORS[openSlug] : undefined
  const accentBorderColor = isTourSlug(openSlug)
    ? TOUR_BORDER_COLORS[openSlug]
    : undefined
  const tourStyle: TourStyle = {
    '--tour-accent-color': accentColor,
    '--tour-accent-border-color': accentBorderColor,
  }
  const tourImageEntries: Array<[TourSlug, TourImage]> = []
  for (const [slug, tourImage] of Object.entries(TOUR_IMAGES)) {
    if (isTourSlug(slug)) {
      tourImageEntries.push([slug, tourImage])
    }
  }

  return (
    <div className={styles.step} style={tourStyle}>
      <div className={styles.content}>
        <div className={styles.left}>
          <div className={styles.leftInner}>
            <Heading as="h1" size="xlarge">
              {step.title}
            </Heading>

            {error && (
              <Notice variant="error" onDismiss={onDismissError}>
                <Notice.Text>{error}</Notice.Text>
              </Notice>
            )}

            <div className={styles.accordion}>
              {step.items.map((item, index) => {
                const isOpen = openSlug === item.slug
                const itemImage = isTourSlug(item.slug)
                  ? TOUR_IMAGES[item.slug]
                  : undefined
                const itemImageVariant = itemImage
                  ? isDarkMode
                    ? itemImage.dark
                    : itemImage.light
                  : undefined

                return (
                  <Collapsible
                    key={item.slug}
                    open={isOpen}
                    onOpenChange={(open): void => {
                      setOpenSlug(open ? item.slug : '')
                      if (open) {
                        BufferTracker.onboardingTourUseCaseClicked({
                          organizationId: organizationId ?? '',
                          useCaseSlug: item.slug,
                          useCaseIndex: index + 1,
                          flowSlug: flowSlug ?? '',
                          flowVersion: flowVersion ?? 0,
                          clientName: Client.PublishWeb,
                          product: Product.Publish,
                        })
                      }
                    }}
                    className={styles.item}
                  >
                    <Collapsible.Trigger asChild>
                      <button type="button" className={styles.itemTrigger}>
                        <span className={styles.chevron}>
                          {isOpen ? (
                            <ChevronUpIcon size="small" />
                          ) : (
                            <ChevronDownIcon size="small" />
                          )}
                        </span>
                        {item.title}
                      </button>
                    </Collapsible.Trigger>
                    <Collapsible.Content>
                      <Flex
                        direction="column"
                        gap="sm"
                        className={styles.itemContent}
                      >
                        <Text color="subtle">{item.description}</Text>
                        {item.link && (
                          <Link href={item.link} className={styles.itemLink}>
                            {item.linkTitle ? (
                              <span translate="no">{item.linkTitle}</span>
                            ) : (
                              'Learn more'
                            )}
                            <ArrowRightIcon size="xsmall" />
                          </Link>
                        )}
                        {itemImageVariant && (
                          <div className={styles.inlinePreview}>
                            <Image
                              src={itemImageVariant.src}
                              srcSet={itemImageVariant.srcSet}
                              alt=""
                              className={styles.inlinePreviewImage}
                            />
                          </div>
                        )}
                      </Flex>
                    </Collapsible.Content>
                  </Collapsible>
                )
              })}
            </div>

            <div className={styles.actions}>
              <Button
                size="large"
                onClick={(): void => onSubmit([])}
                loading={loading}
              >
                Get Started <ArrowRightIcon />
              </Button>
            </div>
          </div>
        </div>

        <div className={styles.preview}>
          <div className={styles.previewStack}>
            {tourImageEntries.map(([slug, tourImage]) => {
              const variant = isDarkMode ? tourImage.dark : tourImage.light
              return (
                <Image
                  key={slug}
                  src={variant.src}
                  srcSet={variant.srcSet}
                  alt=""
                  fetchPriority={
                    slug === step.items[0]?.slug ? 'high' : undefined
                  }
                  className={styles.previewImage}
                  data-visible={openSlug === slug}
                />
              )
            })}
          </div>
          <div className={styles.previewBackground} />
        </div>
      </div>
    </div>
  )
}
